import { useEffect, useState } from "react";
import { auth, db } from "../firebase-config";
import { doc, getDoc, setDoc, updateDoc, arrayUnion, query, where, collection, getDocs, onSnapshot, serverTimestamp } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { runTeamAllocation } from "../util/TeamAllocation";
import Reports from '../util/Reports';
import "../style/Dashboard.css";
import { generatePDFfromElement } from '../util/pdfUtils';
import { useRef } from "react";
import { useLocation } from "react-router-dom";

export async function logTeamHistory(teamId, action, studentId, studentName, teamNumber) {
    // Validate inputs
    if (!teamId || typeof teamId !== 'string') {
        console.error('Invalid teamId:', teamId);
        return;
    }
    try {
        const teamRef = doc(db, "Teams", teamId);

        // First check if document exists
        const docSnap = await getDoc(teamRef);
        if (!docSnap.exists()) {
            // Create document if it doesn't exist
            await setDoc(teamRef, {
                history: [],
                className: teamId,
                createdAt: serverTimestamp()
            });
        }
        
        const historyEntry = {
            text: `${new Date().toLocaleString()} - ${studentName || 'Unknown'} (${studentId || 'N/A'}) ${getActionText(action, teamNumber)}`,
            timestamp: new Date() // Use regular Date instead of serverTimestamp for array elements
        };

        await updateDoc(teamRef, {
            history: arrayUnion(historyEntry)
        });
    } catch (error) {
        console.error("Error in logTeamHistory:", error);
    }
}

function getActionText(action, teamNumber) {
    switch (action) {
        case 'assigned': return `was assigned to team ${teamNumber || 'N/A'}`;
        case 'accepted': return `accepted team ${teamNumber || 'N/A'}`;
        case 'rejected': return `rejected team ${teamNumber || 'N/A'}`;
        case 'reallocated': return `was moved to team ${teamNumber || 'N/A'}`;
        case 'left': return `left team ${teamNumber || 'N/A'}`;
        default: return `performed action: ${action}`;
    }
}

function TeacherDashboard() {
    const [classesCreated, setClassesCreated] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [newClassName, setNewClassName] = useState("");
    const [selectedClass, setSelectedClass] = useState(null);
    const [activeTab, setActiveTab] = useState("All Students");
    const [subnavbarSearchTerm, setSubnavbarSearchTerm] = useState(""); // State for subnavbar search
    const [questionnaires, setQuestionnaires] = useState([]);
    const [students, setStudents] = useState([]); // State to store students
    const [teams, setTeams] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [studentsPerTeam, setStudentsPerTeam] = useState(3); // Default fallback if not fetched
    const [availableTeams, setAvailableTeams] = useState([]);
    const [historyData, setHistoryData] = useState([]);
    const [teamOptionsDialogOpen, setTeamOptionsDialogOpen] = useState(false);
    const [historyVisible, setHistoryVisible] = useState(false);
    const [isJoiningTeam, setIsJoiningTeam] = useState(false);
    const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
    const [archiving, setArchiving] = useState(false);
    const pdfContainerRef = useRef();
    const location = useLocation();
    const [creatingClass, setCreatingClass] = useState(false);

    useEffect(() => {
        // Initial data fetch (runs once on mount)
        const fetchInitialData = async () => {
            const user = auth.currentUser;
            if (!user) return;

            // Fetch teacher classes
            const userDocRef = doc(db, "Users", user.uid);
            const userDoc = await getDoc(userDocRef);
            
            if (userDoc.exists()) {
                const classes = userDoc.data().classesCreated || [];
                setClassesCreated(classes.filter(c => !c.isArchived));
                
                const selectedId = location.state?.classId;
                if (selectedId) {
                    const found = classes.find(c => c.classId === selectedId);
                    if (found) handleClassClick(found);
                }
            }

            // Fetch questionnaires
            const q = query(collection(db, "Questionnaires"), where("createdBy", "==", user.uid));
            const querySnapshot = await getDocs(q);
            setQuestionnaires(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        };

        fetchInitialData();
    }, [location.state]);

    // Fetch students for the selected class
    useEffect(() => {
        if (!selectedClass) return;

        const fetchStudents = async () => {
            const classDocRef = doc(db, "Classes", selectedClass.classId);
            const classDoc = await getDoc(classDocRef);
            if (classDoc.exists()) {
                setStudents(classDoc.data().students || []);
            }
        };

        // Real-time update for Teams when selectedClass changes
        const teamDocRef = doc(db, "Teams", selectedClass.classId);
        const unsubscribe = onSnapshot(teamDocRef, (docSnap) => {
            setTeams(docSnap.exists() ? docSnap.data().teams || [] : []);
        });

        fetchStudents();
        return () => unsubscribe();
    }, [selectedClass]);

    useEffect(() => {
        if (!selectedClass) return;

        // Tab-specific logic
        const handleTabChange = async () => {
            if (activeTab === "All Students") {
                await handleAllStudentsTab(selectedClass);
            }
            
            if (activeTab === "Reallocation") {
                const teamDocRef = doc(db, "Teams", selectedClass.classId);
                const docSnap = await getDoc(teamDocRef);
                setHistoryData(docSnap.exists() ? docSnap.data().history || [] : []);
            }

            if (activeTab === "Teams") {
                const teamDocRef = doc(db, "Teams", selectedClass.classId);
                const teamDoc = await getDoc(teamDocRef);
                setTeams(teamDoc.exists() ? teamDoc.data().teams || [] : []);
            }
        };

        handleTabChange();
    }, [selectedClass, activeTab]); 

    // Function to allocate teams when the "All Students" tab is accessed
    const handleAllStudentsTab = async (manualClass = null) => {
        const selected = manualClass || selectedClass;
        if (!selected) return;

        // Fetch latest students from DB
        const classDocRef = doc(db, "Classes", selected.classId);
        const classDoc = await getDoc(classDocRef);
    
        if (!classDoc.exists()) return;
        const classData = classDoc.data();
        const currentStudents = classData.students || [];
    
        // Find the questionnaire for the selected class
        const questionnaire = questionnaires.find((q) => q.classId === selected.classId);
        if (!questionnaire) return;
    
        const endDate = questionnaire.endDate?.toDate?.() || new Date(questionnaire.endDate);
        const now = new Date();

        const teamDocRef = doc(db, "Teams", selected.classId);
        const teamDoc = await getDoc(teamDocRef);
        const hasTeams = teamDoc.exists();

        if (now <= endDate) {
            setStudents(currentStudents);
            if (hasTeams) {
                setTeams(teamDoc.data().teams || []);
            }
            return;
        }
    
        if (hasTeams) {
            setStudents(currentStudents);
            setTeams(teamDoc.data().teams || []);
            return;
        }
        
        try {
            // Run team allocation for the questionnaire
            const allocatedTeams = await runTeamAllocation(questionnaire.questionnaireId);
            
            // Update students with their team numbers and logging info
            const updatedStudents = await Promise.all(currentStudents.map(async (student) => {
                let matchedTeamNumber = "N/A";
                for (let i = 0; i < allocatedTeams.length; i++) {
                    const team = allocatedTeams[i];
                    if (team.some(member => member.uid === student.uid)) {
                        matchedTeamNumber = i + 1;
                        break;
                    }
                }
                // Get the student's full name from Firestore if not available locally
                let studentName = student.name;
                if (!studentName) {
                    try {
                        const userDoc = await getDoc(doc(db, "Users", student.uid));
                        if (userDoc.exists()) {
                            const userData = userDoc.data();
                            studentName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
                        }
                    } catch (error) {
                        console.error("Error fetching student name:", error);
                    }
                }
                return {
                    ...student,
                    name: studentName || student.name || 'Unknown Student',
                    teamNumber: matchedTeamNumber,
                    hasConfirmedGroup: student.hasConfirmedGroup || false,
                };
            }));

            await updateDoc(classDocRef, {
                students: updatedStudents,
            });

            // Prepare all teams data to be saved in a single document
            const teamsToSave = await Promise.all(
                allocatedTeams.map(async (team, index) => {
                    if (!Array.isArray(team)) {
                        console.error("Invalid team format:", team);
                        return {
                            teamNumber: index + 1,
                            students: []
                        };
                    }
                    const studentsWithNames = await Promise.all(
                        team.map(async (student) => {
                          try {
                            const userDocRef = doc(db, "Users", student.uid);
                            const userDoc = await getDoc(userDocRef);
                            if (userDoc.exists()) {
                              const userData = userDoc.data();
                              return {
                                uid: student.uid,
                                fullName: `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || "Unknown Student",
                                profilePicUrl: userData.profilePicUrl || null
                              };
                            }
                            return {
                              uid: student.uid,
                              fullName: "Unknown Student",
                              profilePicUrl: null
                            };
                          } catch (error) {
                            return {
                              uid: student?.uid || "error",
                              fullName: "Error Loading Student",
                              profilePicUrl: null
                            };
                          }
                        })
                      );
                    return {
                        teamNumber: index + 1,
                        students: studentsWithNames.filter(s => s.uid !== "invalid")
                    };
                })
            );
            // Save ALL teams in a SINGLE document
            await setDoc(teamDocRef, {
                // className: selected.classId,
                teams: teamsToSave,
                lastUpdated: new Date()
            });

            for (const student of updatedStudents) {
                const { uid, name, teamNumber } = student;

                if (teamNumber !== "N/A") {
                    await logTeamHistory(selected.classId, 'assigned', uid, name, teamNumber);
                    await pushNotification(uid, `You have been assigned to Team ${teamNumber} in ${selected.className}`);  // To student
                    await pushNotification(  // To teacher
                        auth.currentUser?.uid,
                        `${name} has been assigned to Team ${teamNumber} in ${selected.className}`
                    );
                }
            }            
            // Update local state
            setStudents(updatedStudents);
            setTeams(teamsToSave);
        } catch (error) {
            console.error("Error allocating teams:", error);
        }
    };    

    const createClass = async () => {
        if (!newClassName.trim() || creatingClass) return;

        setCreatingClass(true); // Prevent duplicate clicks
        const className = newClassName.trim().toUpperCase();
        const user = auth.currentUser;
        const classCode = uuidv4().slice(0, 8);
        const classId = uuidv4();

        try {
            const classDocRef = doc(db, "Classes", classId);
            await setDoc(classDocRef, {
                className: className,
                classId: classId,
                inviteCode: classCode,
                teacherId: user.uid,
                students: []
            });

            const userDocRef = doc(db, "Users", user.uid);
            await updateDoc(userDocRef, {
                classesCreated: arrayUnion({ className, inviteCode: classCode, classId })
            });

            await updateDoc(userDocRef, {
                notifications: arrayUnion({
                    message: `You have created the class ${className}`,
                    timestamp: new Date(),
                    seen: false
                })
            });

            setClassesCreated(prev => [...prev, { className, inviteCode: classCode, classId }]);
            setNewClassName("");
        } catch (error) {
            console.error("Error creating class:", error);
        } finally {
            setCreatingClass(false);
        }
    };

    const handleClassClick = async (classData) => {
        setSelectedClass(null);         // Immediately clear selection
        setStudents([]);                // Clear students
        setTeams([]);                   // Clear teams
        setHistoryData([]);             // Clear history
        setActiveTab("All Students");    // Optional: reset active tab if you want
    
        // Now actually set new class
        setSelectedClass(classData);
    
        // Then fetch teams
        const teamDocRef = doc(db, "Teams", classData.classId);
        const teamDoc = await getDoc(teamDocRef);
        if (teamDoc.exists()) {
            setTeams(teamDoc.data().teams || []);
        } else {
            setTeams([]);
        }
    };

    const [sortConfig, setSortConfig] = useState({
        key: "name",       // Default sorting by name
        direction: "asc",  // a -> z
    });

    const handleSort = (key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                // Same column → reverse direction
                return {
                    key,
                    direction: prev.direction === "asc" ? "desc" : "asc",
                };
            } else {
                // Switch to new column → Default ascending order
                return {
                    key,
                    direction: "asc",
                };
            }
        });
    };

    const handleJoinTeam = async (team) => {
        if (!selectedStudent || !selectedClass || isJoiningTeam) return;
        setIsJoiningTeam(true); // Prevent multiple clicks
    
        try {
            // 1. Update the Teams collection
            const teamRef = doc(db, "Teams", selectedClass.classId);
            const teamDoc = await getDoc(teamRef);
            if (!teamDoc.exists()) return;
    
            // Check if student is already in this team
            const currentTeam = teamDoc.data().teams.find(t => t.teamNumber === team.teamNumber);
            if (currentTeam.students.some(s => s.uid === selectedStudent.uid)) {
                setIsJoiningTeam(false);
                return;
            }

            const data = teamDoc.data();
            const updatedTeams = data.teams.map(t => {
                if (t.teamNumber === team.teamNumber) {
                    return {
                        ...t,
                        students: [...t.students, {
                            uid: selectedStudent.uid,
                            fullName: selectedStudent.name
                        }]
                    };
                }
                // Remove student from any other teams they might be in
                return {
                    ...t,
                    students: t.students.filter(s => s.uid !== selectedStudent.uid)
                };
            });
    
            await updateDoc(teamRef, { teams: updatedTeams });
            const notifyTeamMembers = async (team, newMember) => {
                for (const member of team.students) {
                    if (member.uid === newMember.uid) continue; // dont notify myself
                    const memberRef = doc(db, "Users", member.uid);
                    await updateDoc(memberRef, {
                        notifications: arrayUnion({
                            message: `${newMember.name} has joined your team in ${selectedClass.className}`,
                            timestamp: new Date(),
                            seen: false
                        })
                    });
                }
              };
              await notifyTeamMembers(
                updatedTeams.find(t => t.teamNumber === team.teamNumber),
                selectedStudent
              );              
    
            // 2. Update the Classes collection (instead of Users)
            const classRef = doc(db, "Classes", selectedClass.classId);
            const classDoc = await getDoc(classRef);
            
            if (!classDoc.exists()) return;
            
            const classData = classDoc.data();
            const updatedStudentList = classData.students.map(s => {
                if (s.uid === selectedStudent.uid) {
                    return {
                        ...s,
                        teamNumber: team.teamNumber,
                        hasConfirmedGroup: false // Default to false when joining a new team
                    };
                }
                return s;
            });
    
            await updateDoc(classRef, { students: updatedStudentList });
            
            // 3. Log the team reallocation event
            await logTeamHistory(selectedClass.classId, 'reallocated', selectedStudent.uid, selectedStudent.name, team.teamNumber);
            
            await pushNotification(  // To student
                selectedStudent.uid,
                `You have been moved to Team ${team.teamNumber} in ${selectedClass.className}`
            );

            await pushNotification(  // To teacher
                auth.currentUser?.uid,
                `${selectedStudent.name} has been moved to Team ${team.teamNumber} in ${selectedClass.className}`
            );
            
            alert(`${selectedStudent.name} has joined Team ${team.teamNumber}.`);
        } catch (error) {
            console.error("Failed to join team:", error);
            alert("Failed to join team. Please try again.");
        } finally {
            setIsJoiningTeam(false); // Re-enable button
        }
    };

    const sortedStudents = [...students].sort((a, b) => {
        const { key, direction } = sortConfig;
    
        const valA = a[key];
        const valB = b[key];
    
        const isANA = valA === "N/A";
        const isBNA = valB === "N/A";
    
        // Always put N/A at the top
        if (isANA && !isBNA) return -1;
        if (!isANA && isBNA) return 1;
    
        // Regular value comparison
        const valueA = (valA ?? "").toString().toLowerCase();
        const valueB = (valB ?? "").toString().toLowerCase();
    
        if (valueA < valueB) return direction === "asc" ? -1 : 1;
        if (valueA > valueB) return direction === "asc" ? 1 : -1;
    
        // Secondary sort by name
        const nameA = (a.name ?? "").toLowerCase();
        const nameB = (b.name ?? "").toLowerCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
    
        return 0;
    });

    // Correct function to open the dialog
    const handleShowTeamOptions = (student) => {
        setSelectedStudent(student);
        const available = teams.filter(team => team.students.length < studentsPerTeam);
        setAvailableTeams(available);
        setTeamOptionsDialogOpen(true); // Correctly using the state updater function
    };

    const [readyToGenerate, setReadyToGenerate] = useState(false);

    const handleArchiveClass = async () => {
        if (!selectedClass) return;
        setArchiving(true);
        setReadyToGenerate(false);
      
        try {
          // Wait until Reports tells us it's fully rendered
          await new Promise((resolve) => {
            const timeout = setTimeout(() => {
              console.warn("⚠️ Timeout fallback: proceeding with PDF after 10s.");
              resolve();
            }, 10000); // 10 seconds max wait
          
            const interval = setInterval(() => {
              if (readyToGenerate) {
                clearTimeout(timeout);
                clearInterval(interval);
                resolve();
              }
            }, 300);
          });
          
      
          const pdfElement = pdfContainerRef.current;
          await generatePDFfromElement(pdfElement, `${selectedClass.className}-Team-Overview.pdf`);
      
          // Archive steps...
          const classRef = doc(db, "Classes", selectedClass.classId);
          await updateDoc(classRef, {
            isArchived: true,
            archivedAt: serverTimestamp()
          });
      
          const userRef = doc(db, "Users", auth.currentUser.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            const updatedClasses = userDoc.data().classesCreated.map(c =>
              c.classId === selectedClass.classId ? { ...c, isArchived: true } : c
            );
            await updateDoc(userRef, { classesCreated: updatedClasses });
          }
      
          setClassesCreated(prev => prev.filter(c => c.classId !== selectedClass.classId));
          setSelectedClass(null);
          alert("Class archived and report downloaded successfully!");
        } catch (error) {
          console.error("Error archiving class:", error);
          alert("Failed to archive class. Please try again.");
        } finally {
          setArchiving(false);
          setArchiveDialogOpen(false);
        }
      };
    
    const pushNotification = async (userId, message) => {
        if (!userId || typeof userId !== 'string') {
            console.error('Invalid user ID:', userId);
            return; // Skip sending notification if user ID is invalid
        }

        try {
            const userRef = doc(db, "Users", userId);
            await updateDoc(userRef, {
                notifications: arrayUnion({
                    message,
                    timestamp: new Date(),
                    seen: false
                })
            });
        } catch (error) {
            console.error(`Failed to send notification to user ${userId}:`, error);
        }
    };

    const ArchivingOverlay = ({ text = "Archiving and generating PDF…" }) => (
        <div className="archiving-overlay">
          <div className="archiving-spinner" />
          <p className="archiving-message">{text}</p>
        </div>
      );

      const LazyTeamMember = ({ uid, name }) => {
        const [profilePicUrl, setProfilePicUrl] = useState(null);
      
        useEffect(() => {
          const fetchProfilePic = async () => {
            const userDoc = await getDoc(doc(db, "Users", uid));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              setProfilePicUrl(userData.profilePicUrl || null);
            }
          };
          fetchProfilePic();
        }, [uid]);
      
        const initials = name
          ?.split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .slice(0, 2);
      
        return (
          <div className="team-member">
            {profilePicUrl ? (
              <img src={profilePicUrl} alt="Profile" className="member-avatar profile-image" />
            ) : (
              <div className="member-avatar initials-avatar">{initials}</div>
            )}
            <span>{name}</span>
          </div>
        );
      };
      
    return (
        <div className="dashboard-container">
            {/* Left Sidebar - Class List */}
            <div className="sidebar">
            <div className="sidebar-inner">
                <input 
                    type="text" 
                    placeholder="Search for a class..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <input 
                    type="text" 
                    placeholder="Enter class name (e.g., IFB399)" 
                    value={newClassName}
                    onChange={(e) => setNewClassName(e.target.value)}
                />
                <button onClick={createClass}>Create Class</button>
            </div>
                <ul>
                    {classesCreated
                        .filter(c => c?.className && !c?.isArchived)  // <- safer filtering
                        .filter(c => c.className.toLowerCase().includes(searchTerm.toLowerCase()))
                        .map((c, index) => (
                            <li 
                                key={index} 
                                className={selectedClass?.inviteCode === c.inviteCode ? "selected" : ""}
                                onClick={() => handleClassClick(c)}
                            >
                                {c.className}
                            </li>
                        ))}
                </ul>
            </div>

            {/* Right Panel - Class Overview */}
            <div className="class-content">
                {selectedClass ? (
                    <>
                        <h2>Dashboard</h2>

                        {/* Subnavbar for Class Tabs */}
                        <div className="subnavbar">
                            {["All Students", "Teams", "Reallocation", "Reports", "Class Details"].map(tab => (
                                <button 
                                    key={tab}
                                    className={activeTab === tab ? "active" : ""}
                                    onClick={() => setActiveTab(tab)}
                                >
                                    {tab}
                                </button>
                            ))}

                            {/* Search Bar */}
                            <div className="search-bar">
                                <input 
                                    type="text" 
                                    placeholder="Search..." 
                                    value={subnavbarSearchTerm}
                                    onChange={(e) => setSubnavbarSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Tab Content */}
                        <div className="tab-content">
                            {activeTab === "All Students" && (
                                <div className="students-list">
                                    <table className="student-table">
                                        <thead>
                                            <tr>
                                                <th onClick={() => handleSort("name")} style={{ cursor: "pointer" }}>
                                                    Student {sortConfig.key === "name" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
                                                </th>
                                                <th>Year Level</th>
                                                <th>ID</th>
                                                <th>Discipline</th>
                                                <th onClick={() => handleSort("teamNumber")} style={{ cursor: "pointer" }}>
                                                    Group {sortConfig.key === "teamNumber" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedStudents.map((student, index) => (
                                                <tr key={index}>
                                                    <td>{student.name}</td>
                                                    <td>{student.yearLevel}</td>
                                                    <td>{student.id}</td>
                                                    <td>{student.discipline}</td>
                                                    <td>
                                                    <button
                                                        className={
                                                        "group-button " +
                                                        (student.teamNumber === "N/A"
                                                            ? "group-na"
                                                            : student.hasConfirmedGroup
                                                            ? "group-confirmed"
                                                            : "group-pending")
                                                        }
                                                        onClick={() => {
                                                            setSelectedStudent(student);
                                                            setActiveTab("Teams")}}
                                                    >
                                                        {student.teamNumber}
                                                    </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            {activeTab === "Teams" && (
                                <div className="teams-container">
                                    {teams
                                        .slice()
                                        .sort((a, b) => a.teamNumber - b.teamNumber)
                                        .map((team) => (
                                            <div className="team-column" key={team.teamNumber}>
                                                <div className="team-header">Team {team.teamNumber}</div>
                                                {team.students.map((student) => (
                                                    <LazyTeamMember key={student.uid} uid={student.uid} name={student.fullName} />
                                                ))}

                                                {selectedStudent &&
                                                    !selectedStudent.hasConfirmedGroup &&
                                                    team.students.length < studentsPerTeam &&
                                                    !teams.some(t =>
                                                        t.students.some(s => s.uid === selectedStudent.uid)
                                                    ) && (
                                                        <button
                                                            onClick={() => handleJoinTeam(team, "Reallocation")}
                                                            className="join-button"
                                                            disabled={isJoiningTeam}
                                                        >
                                                            {isJoiningTeam ? "Joining..." : "Join"}
                                                        </button>
                                                    )}
                                            </div>
                                        ))}
                                </div>
                            )}
                            {activeTab === "Reallocation" && (
                                <div className="reallocation-container">
                                    
                                    {(() => {
                                        // Questionnaire in progress
                                        const now = new Date();
                                        const q = questionnaires.find(q => q.classId === selectedClass?.classId);
                                        const start = q ? (q.startDate.toDate?.() ?? new Date(q.startDate)) : null;
                                        const end   = q ? (q.endDate.toDate?.()   ?? new Date(q.endDate))   : null;
                                        const ongoing = q && start <= now && now <= end;

                                        if (ongoing) {
                                            return (
                                            <h3>Questionnaire period is still ongoing; grouping has not started yet.</h3>
                                            );
                                        }

                                        // Someone has no group
                                        const unassigned = students.filter(s => s.teamNumber === "N/A");
                                        if (unassigned.length > 0) {
                                            return (
                                            <>
                                                <h3>Unassigned Students</h3>
                                                <ul>
                                                {unassigned.map((s, i) => (
                                                    <li key={i}
                                                        className="unassigned-student"
                                                    >
                                                    <span>{s.name}</span>
                                                    <button
                                                        className="options-button"
                                                        onClick={() => handleShowTeamOptions(s)}
                                                    >
                                                        ⋮
                                                    </button>
                                                    </li>
                                                ))}
                                                </ul>
                                            </>
                                            );
                                        }

                                        // All grouping completed
                                        return <h3>All students have been assigned to a group.</h3>;
                                        })()}

                                    {/* Pop-up window showing groups that are not full */}
                                    {teamOptionsDialogOpen && (
                                        <div className="dialog-overlay">
                                            <div className="dialog-box">
                                                <h4 className="dialog-heading">Select a team to assign</h4>
                                                <ul className="team-list">
                                                    {availableTeams.map((team, index) => (
                                                        <li key={index}>
                                                            <button 
                                                                className="team-button"
                                                                onClick={() => handleJoinTeam(team, "Reallocation")}
                                                                disabled={isJoiningTeam}
                                                            >
                                                            {isJoiningTeam ? "Joining..." : `Team ${team.teamNumber}`}
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                                <button
                                                    className="close-button" 
                                                    onClick={() => setTeamOptionsDialogOpen(false)}
                                                >
                                                    Close
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    <button className="btn btn-toggle-history" onClick={() => setHistoryVisible(!historyVisible)}>
                                        {historyVisible ? "Hide History" : "Show History"}
                                    </button>
                                    {historyVisible && (
                                        <>
                                        <div className="history-overlay">
                                            <div className="history-box">
                                                <h1>History</h1>
                                                <div className='history-container'>
                                                    {historyData.length > 0 ? (
                                                        historyData.map((entry, index) => (
                                                            <p key={index}>{entry.text}</p>
                                                        ))
                                                    ) : (
                                                        <p>No history data available.</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        </>
                                    )}
                                </div>
                            )}
                            {activeTab === "Reports" && (
                                <Reports
                                    teams={teams}
                                    students={students}
                                    className={selectedClass?.className || "Class"}
                                    questionnaireId={questionnaires.find(q => q.classId === selectedClass?.classId)?.questionnaireId}
                                    autoGeneratePDF={false}   // Force disable for main panel
                                />
                                )}
                            {activeTab === "Class Details" && (
                                <div className="class-details">
                                    {archiveDialogOpen && (
                                        <div className="archive-dialog-overlay">
                                            <div className="archive-dialog-box">
                                                <h3>Archive Class</h3>
                                                <p>Are you sure you want to archive this class?</p>
                                                
                                                <div className="archive-dialog-actions">
                                                    <button
                                                        className="archive-cancel-button"
                                                        onClick={() => setArchiveDialogOpen(false)}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        className="archive-confirm-button"
                                                        onClick={handleArchiveClass}
                                                        disabled={archiving}
                                                    >
                                                        {archiving ? 'Archiving...' : 'Archive'}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <h3>Class Name: {selectedClass.className}</h3>
                                    
                                    <div className="invite-code-container">
                                        <p className="invite-code-label">Invite Code:</p>
                                        <div className="invite-code-display">
                                            {selectedClass.inviteCode}
                                        </div>
                                        <button 
                                            className="btn btn-copy"
                                            onClick={() => {
                                                navigator.clipboard.writeText(selectedClass.inviteCode);
                                                alert('Invite code copied to clipboard!');
                                            }}
                                        >
                                            Copy
                                        </button>
                                        <button
                                            className="btn btn-share" 
                                            onClick={() => {
                                                const shareText = `Join my class ${selectedClass.className} on Grouplet using this invite code: ${selectedClass.inviteCode}`;
                                                if (navigator.share) {
                                                    navigator.share({
                                                        title: 'Join My Class',
                                                        text: shareText
                                                    }).catch(err => {
                                                        console.log('Error sharing:', err);
                                                        navigator.clipboard.writeText(shareText);
                                                        alert('Share content copied to clipboard!');
                                                    });
                                                } else {
                                                    navigator.clipboard.writeText(shareText);
                                                    alert('Share content copied to clipboard!');
                                                }
                                            }}
                                        >
                                            Share
                                        </button>
                                    </div>
                                    
                                    <p className="student-count">Number of Students: {students.length}</p>
                                    <button 
                                        className="btn btn-archive"
                                        onClick={() => setArchiveDialogOpen(true)}
                                    >
                                        Archive Class
                                    </button>
                                </div>
                            )}
                        </div>
                        </>
                ) : (
                    <p>Select a class to view details.</p>
                )}
            </div>
            {/* Hidden Reports container for PDF generation */}
            {/* Only render hidden PDF container when archiving */}
            {archiving &&  (
            <div className="pdf-generation-container">
                <div 
                ref={pdfContainerRef} 
                className="pdf-report-container" 
                >
                <Reports
                    teams={teams}
                    students={students}
                    className={selectedClass?.className || "Class"}
                    questionnaireId={questionnaires.find(q => q.classId === selectedClass?.classId)?.questionnaireId}
                    onReadyToRender={() => setReadyToGenerate(true)}
                    hideControls={true}  // optional: hides buttons and toggles during auto-PDF
                    isArchived={true}
                />  
                </div>
            </div>
            )}
            {archiving && <ArchivingOverlay />}
        </div>
    );
}

export default TeacherDashboard;